import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { BringgInput } from '@bringg/react-components';
import { BaseRecord, BringgReactTable } from '@bringg-frontend/bringg-react-table';
import { CellProps, Column, useFlexLayout, useTable } from 'react-table';

import type { ColumnLabelOverrides, ColumnTableId } from '../utils/column-label-overrides';
import type { ColumnLabelColumn } from '../column-label-definitions';
import ColumnLabelsTableContainer from './column-labels-table-container/column-labels-table-container';
import ColumnLabelsTableRow from './column-labels-table-row/column-labels-table-row';

import styles from './columns-labels-table.module.scss';

export const COLUMN_LABEL_OVERRIDE_MAX_LENGTH = 250;

export type ColumnLabelTableRecord = ColumnLabelColumn &
	BaseRecord & {
		overrideValue: string;
	};

interface ColumnsLabelsTableProps {
	filteredColumns: ColumnLabelColumn[];
	overrides: ColumnLabelOverrides;
	activeTableId: ColumnTableId;
	activeLanguageCode: string;
	activeLanguageName: string;
	onLabelChange: (columnId: string, value: string) => void;
}

const ColumnsLabelsTable: React.FC<ColumnsLabelsTableProps> = ({
	filteredColumns,
	overrides,
	activeTableId,
	activeLanguageCode,
	activeLanguageName,
	onLabelChange
}) => {
	const { t } = useTranslation();

	const data = useMemo<ColumnLabelTableRecord[]>(
		() =>
			filteredColumns.map(column => ({
				...column,
				overrideValue: overrides?.[activeTableId]?.[column.id]?.title?.[activeLanguageCode] ?? ''
			})),
		[activeLanguageCode, activeTableId, filteredColumns, overrides]
	);

	const columns = useMemo<Column<ColumnLabelTableRecord>[]>(
		() => [
			{
				Header: () => <>{t('WEB_USER_INTERFACE.COLUMN_LABELS.BRINGG_TERM')}</>,
				id: 'bringg_term',
				accessor: column => column.label,
				Cell: ({ row }: CellProps<ColumnLabelTableRecord>) => <span>{row.original.label}</span>
			},
			{
				Header: () => <>{t('WEB_USER_INTERFACE.COLUMN_LABELS.YOUR_LABEL', { language: activeLanguageName })}</>,
				id: 'your_label',
				accessor: column => column.overrideValue,
				Cell: ({ row }: CellProps<ColumnLabelTableRecord>) => {
					const column = row.original;
					const bringgTermLabel = column.label;

					return (
						<BringgInput
							placeholder={bringgTermLabel}
							value={column.overrideValue}
							maxLength={COLUMN_LABEL_OVERRIDE_MAX_LENGTH}
							onChange={event =>
								onLabelChange(column.id, event.target.value.slice(0, COLUMN_LABEL_OVERRIDE_MAX_LENGTH))
							}
							data-test-id={`column-label-input-${column.id}`}
						/>
					);
				}
			}
		],
		[activeLanguageName, onLabelChange]
	);

	const defaultColumn = useMemo(
		() => ({
			minWidth: 120,
			width: 300
		}),
		[]
	);

	const tableInstance = useTable<ColumnLabelTableRecord>(
		{
			columns,
			data,
			defaultColumn
		},
		useFlexLayout
	);

	return (
		<div className={styles.tableWrapper} data-test-id="column-labels-table">
			<BringgReactTable<ColumnLabelTableRecord>
				tableInstance={tableInstance}
				TableContainerComponent={ColumnLabelsTableContainer}
				RenderRow={ColumnLabelsTableRow}
				className={styles.columnLabelsTable}
			/>
		</div>
	);
};

export default ColumnsLabelsTable;
